import React, { FC } from "react";
import styled, { css } from "styled-components";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { UI_SIZE } from "../../enums";
import { castingSupported } from "../../service/cast-service";
import { usePlayerStore } from "../../store/usePlayerStore";
import { focusColor } from "../../styles/variables";

import { ChatButton } from "./ChatButton";
import { Chromecast } from "./Chromecast";
import Settings from "./Settings";
import { VideoTitle } from "./VideoTitle";

const ControlBar = styled.div<{
  controlsVisible: boolean;
  isMobile: boolean;
}>`
  background: linear-gradient(180deg, #000 0%, rgb(0 0 0 / 0%) 100%);
  display: flex;
  justify-content: space-between;
  left: 0;
  padding: ${({ isMobile }) => (isMobile ? "12px 8px" : "12px 20px")};
  pointer-events: none;
  position: absolute;
  right: 0;
  transform: ${({ controlsVisible }) =>
    controlsVisible ? "translate(0,0)" : "translate(0,-100%)"};
  transition: transform 0.3s ease-in-out;
  z-index: 100;

  google-cast-launcher {
    --disconnected-color: #fff;
    box-sizing: content-box;
    cursor: pointer;
    height: 26px;
    padding: 3px;
    pointer-events: auto;
    width: 26px;

    &:focus-visible {
      outline: 1px solid ${focusColor} !important;
    }
  }
`;

const WrapDiv = styled.div`
  display: flex;

  > * + * {
    margin-left: 8px;
  }
`;

const TopRightControls = styled.div<{ uiSize: UI_SIZE }>`
  display: flex;
  flex: 1;
  height: 100%;

  > * {
    margin: 0 0 10px 16px;
  }

  ${({ uiSize }) =>
    uiSize === UI_SIZE.LARGE
      ? css`
          flex-direction: row;
          justify-content: flex-end;
        `
      : css`
          align-items: flex-end;
          flex-direction: column-reverse;
        `}
`;

export const TopControlBar: FC = () => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const isChatButtonEnabled = useStoreState.isChatButtonEnabled();
  const isControlsVisible = useStoreState.isControlsVisible();
  const uiSize = useStoreState.uiSize();

  const {
    chromecast,
    language,
    title: embedParamTitle
  } = useEmbedParametersContext();
  const { title } = useMediaContext();

  const showMobileControlBar = device.isMobile || uiSize === UI_SIZE.XSMALL;

  const emptyControlBar =
    !(embedParamTitle && title?.[language]) &&
    !isChatButtonEnabled &&
    !castingSupported() &&
    !showMobileControlBar;

  return emptyControlBar ? null : (
    <ControlBar
      controlsVisible={isControlsVisible}
      isMobile={showMobileControlBar}>
      {embedParamTitle && <VideoTitle />}
      <TopRightControls uiSize={uiSize}>
        {isChatButtonEnabled && <ChatButton />}
        <WrapDiv>
          <>
            <Chromecast chromecast={chromecast} />
            {showMobileControlBar && (
              <Settings icon="controlsSettingsIcon" settingsName="mobile" />
            )}
          </>
        </WrapDiv>
      </TopRightControls>
    </ControlBar>
  );
};
